import type { PriceId } from '@iheartjane/types';
import { createExternalStore, SessionStorage } from '@iheartjane/utils';
import { useSyncExternalStore } from 'react';

type Prices = Partial<Record<`price_${PriceId}`, string | number | null>>;

/** The add request's fields plus exactly what `moeAddToBag` reads. */
type ReplacementCartProduct = Prices & {
  aggregate_rating?: number | null;
  brand?: string | null;
  brand_special_id?: number | null;
  category: string | null;
  count: number;
  has_brand_discount?: boolean | null;
  name: string;
  price_id: PriceId;
  product_brand_id?: number | null;
  product_id: number;
  recommended: boolean;
  review_count?: number | null;
};

export interface ReplacementCart {
  product: ReplacementCartProduct;
  store: { id: number | string; name: string };
}

type ReplaceableMenuProduct = Prices &
  Omit<
    ReplacementCartProduct,
    'count' | 'price_id' | 'product_id' | 'recommended'
  > & {
    id: number;
    recommended?: boolean;
  };

const REPLACEMENT_CART_KEY = 'cart-replacement';

const { notify, stable, subscribe } =
  createExternalStore<ReplacementCart | null>();

export const toReplacementCart = ({
  count,
  menuProduct,
  price_id,
  store,
}: {
  count: number;
  menuProduct: ReplaceableMenuProduct;
  price_id: PriceId;
  store: ReplacementCart['store'];
}): ReplacementCart => ({
  product: {
    aggregate_rating: menuProduct.aggregate_rating,
    brand: menuProduct.brand,
    brand_special_id: menuProduct.brand_special_id,
    category: menuProduct.category,
    count,
    has_brand_discount: menuProduct.has_brand_discount,
    name: menuProduct.name,
    [`price_${price_id}`]: menuProduct[`price_${price_id}`],
    price_id,
    product_brand_id: menuProduct.product_brand_id,
    product_id: menuProduct.id,
    recommended: !!menuProduct.recommended,
    review_count: menuProduct.review_count,
  },
  store: { id: store.id, name: store.name },
});

const parse = (stored: string | null): ReplacementCart | null => {
  if (!stored) return null;

  try {
    return JSON.parse(stored) as ReplacementCart;
  } catch {
    return null;
  }
};

export const getReplacementCart = (): ReplacementCart | null =>
  stable(parse(SessionStorage.get(REPLACEMENT_CART_KEY)));

export const setReplacementCart = (replacementCart: ReplacementCart) => {
  SessionStorage.set(REPLACEMENT_CART_KEY, JSON.stringify(replacementCart));
  notify();
};

export const discardReplacementCart = () => {
  SessionStorage.remove(REPLACEMENT_CART_KEY);
  notify();
};

export const useReplacementCart = () =>
  useSyncExternalStore(subscribe, getReplacementCart, () => null);
